import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { Search, MoreVertical, ExternalLink, Eye, EyeOff, Trash2, UserCheck, Gift, Star, CreditCard, X, LogIn, ChevronRight, Mail, MapPin, Briefcase, Globe, Linkedin, Clock, DollarSign, Calendar, MessageSquare, CheckCircle, XCircle, Send, AlertTriangle, Ban } from 'lucide-react';

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-[100] flex items-center gap-2 px-5 py-3 rounded-xl shadow-xl text-white text-sm font-medium ${type === 'success' ? 'bg-[#004D35]' : 'bg-red-600'}`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
      {message}
    </div>
  );
};

const AdvisorDetailPanel = ({ advisor, onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [activity, setActivity] = useState([]);
  const [impersonationSessions, setImpersonationSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [priceOverride, setPriceOverride] = useState(advisor.price_override || '');
  const [savingPrice, setSavingPrice] = useState(false);
  const [toast, setToast] = useState(null);
  const [resendingInvite, setResendingInvite] = useState(false);

  const showToast = useCallback((message, type = 'success') => setToast({ message, type }), []);

  const handleResendInvite = async () => {
    setResendingInvite(true);
    try {
      const res = await fetch(`/api/admin/advisors/${advisor.id}/resend-invite`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok) {
        showToast(data.message || 'Invitation resent');
      } else {
        showToast(data.error || 'Failed to resend invitation', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    } finally {
      setResendingInvite(false);
    }
  };

  useEffect(() => {
    if (advisor) {
      fetchDetail();
    }
  }, [advisor]);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/advisors/${advisor.id}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setActivity(data.activity || []);
        setImpersonationSessions(data.impersonation_sessions || []);
      }
    } catch (error) {
      console.error('Error fetching advisor detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrice = async () => {
    setSavingPrice(true);
    try {
      const response = await fetch(`/api/admin/advisors/${advisor.id}/price`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price_override: priceOverride === '' ? null : parseInt(priceOverride) })
      });
      if (response.ok) {
        alert('Price override updated');
        onRefresh();
      }
    } catch (error) {
      console.error('Error updating price:', error);
    } finally {
      setSavingPrice(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    const consequenceMap = {
      active: 'The advisor will be visible in search results.',
      paused: 'The advisor will remain visible but marked as paused.',
      suspended: 'The advisor will be hidden from search and their subscription may be affected.'
    };

    if (!window.confirm(`Set ${advisor.first_name} to ${newStatus}? ${consequenceMap[newStatus]} This cannot be undone automatically.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/advisors/${advisor.id}/status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        alert(`Status updated to ${newStatus}`);
        onRefresh();
        onClose();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getCompletionColor = (pct) => {
    if (pct >= 80) return 'bg-green-100 text-green-800';
    if (pct >= 50) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (!advisor) return null;

  return (
    <>
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 max-w-full flex">
        <div className="w-screen max-w-2xl bg-white shadow-xl flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
            <div className="flex items-center space-x-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-white bg-primary`}>
                {advisor.first_name?.[0]}{advisor.last_name?.[0]}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  {advisor.first_name} {advisor.last_name}
                  <span className="ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary uppercase">
                    {advisor.category_slug || advisor.specialty}
                  </span>
                </h2>
                <div className="flex items-center mt-1 space-x-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    advisor.suspended_at ? 'bg-red-100 text-red-800' : 
                    advisor.pause_started_at ? 'bg-yellow-100 text-yellow-800' : 
                    'bg-green-100 text-green-800'
                  }`}>
                    {advisor.suspended_at ? 'Suspended' : advisor.pause_started_at ? 'Paused' : 'Active'}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCompletionColor(advisor.profile_completion_pct)}`}>
                    {advisor.profile_completion_pct || 0}% Complete
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full">
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          <div className="flex border-b border-gray-200 bg-white">
            {['profile', 'subscription', 'activity'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                  activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Bio</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{advisor.bio || 'No bio provided'}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
                    <div className="flex items-center text-gray-500 mb-1">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span className="text-xs font-medium uppercase">Location</span>
                    </div>
                    <div className="text-sm text-gray-900">{advisor.location || 'Not set'}</div>
                  </div>
                  <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
                    <div className="flex items-center text-gray-500 mb-1">
                      <Clock className="w-4 h-4 mr-2" />
                      <span className="text-xs font-medium uppercase">Experience</span>
                    </div>
                    <div className="text-sm text-gray-900">{advisor.years_experience || 0} years</div>
                  </div>
                  <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
                    <div className="flex items-center text-gray-500 mb-1">
                      <Globe className="w-4 h-4 mr-2" />
                      <span className="text-xs font-medium uppercase">Website</span>
                    </div>
                    <div className="text-sm">
                      {advisor.website_url ? (
                        <a href={advisor.website_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center">
                          Link <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      ) : 'Not set'}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
                    <div className="flex items-center text-gray-500 mb-1">
                      <Linkedin className="w-4 h-4 mr-2" />
                      <span className="text-xs font-medium uppercase">LinkedIn</span>
                    </div>
                    <div className="text-sm">
                      {advisor.linkedin_url ? (
                        <a href={advisor.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center">
                          Profile <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      ) : 'Not set'}
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase block mb-1">Headline</span>
                    <div className="text-sm text-gray-900">{advisor.title || 'Not set'}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase block mb-1">Hourly Rate</span>
                      <div className="text-sm text-gray-900">{advisor.hourly_rate ? `$${advisor.hourly_rate}` : 'Not set'}</div>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500 uppercase block mb-1">Free Consultation</span>
                      <div className="text-sm text-gray-900">{advisor.free_consultation ? 'Yes' : 'No'}</div>
                    </div>
                  </div>
                </div>

                {(advisor.status === 'pending_setup' || advisor.invitation_sent === false || advisor.invitationSent === false) && (
                  <div className="space-y-2">
                    {(advisor.invitation_warning || advisor.warning) && <p role="status" className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">{advisor.invitation_warning || advisor.warning}</p>}
                    <button
                      onClick={handleResendInvite}
                      disabled={resendingInvite}
                      className="flex items-center justify-center w-full px-4 py-3 bg-amber-50 border border-amber-300 rounded-2xl text-sm font-semibold text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-50"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {resendingInvite ? 'Sending…' : 'Resend invitation'}
                    </button>
                  </div>
                )}

                <a 
                  href={`/advisor/${advisor.profile_slug || advisor.id}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-full px-4 py-3 bg-white border border-gray-200 rounded-2xl text-sm font-medium text-primary hover:bg-gray-50 transition-colors"
                >
                  View public profile <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </div>
            )}

            {activeTab === 'subscription' && (
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Plan Details</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Current Plan</span>
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-bold uppercase">
                      {advisor.plan_type || 'Beta'}
                    </span>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
                  <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Pricing Override</h3>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase block mb-1">Monthly Price</label>
                    <div className="flex space-x-2">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input 
                          type="number" 
                          value={priceOverride}
                          onChange={(e) => setPriceOverride(e.target.value)}
                          placeholder="Global rate"
                          className="w-full pl-7 pr-4 py-2 border border-gray-200 rounded-xl text-sm"
                        />
                      </div>
                      <button 
                        onClick={handleUpdatePrice}
                        disabled={savingPrice}
                        className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary-dark disabled:opacity-50"
                      >
                        {savingPrice ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-400 italic">Leave blank to use global rate</p>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-3">
                  <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Stripe Info</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500 uppercase">Customer ID</span>
                      <span className="text-xs font-mono text-gray-400">{advisor.stripe_customer_id || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500 uppercase">Subscription ID</span>
                      <span className="text-xs font-mono text-gray-400">{advisor.stripe_subscription_id || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
                  <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Status Controls</h3>
                  <div className="grid grid-cols-1 gap-3">
                    {advisor.suspended_at || advisor.pause_started_at ? (
                      <button 
                        onClick={() => handleStatusChange('active')}
                        className="w-full px-4 py-3 bg-green-600 text-white rounded-2xl text-sm font-bold hover:bg-green-700 transition-colors"
                      >
                        Set Active
                      </button>
                    ) : null}
                    {!advisor.pause_started_at && !advisor.suspended_at ? (
                      <button 
                        onClick={() => handleStatusChange('paused')}
                        className="w-full px-4 py-3 bg-amber-400 text-white rounded-2xl text-sm font-bold hover:bg-amber-500 transition-colors"
                      >
                        Set Paused
                      </button>
                    ) : null}
                    {!advisor.suspended_at ? (
                      <button 
                        onClick={() => handleStatusChange('suspended')}
                        className="w-full px-4 py-3 border border-red-600 text-red-600 rounded-2xl text-sm font-bold hover:bg-red-50 transition-colors"
                      >
                        Set Suspended
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="space-y-4">
                {activity.length === 0 && impersonationSessions.length === 0 ? (
                  <div className="bg-white p-12 text-center rounded-2xl border border-gray-200 shadow-sm">
                    <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No admin activity for this advisor yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {[...activity, ...impersonationSessions.map(s => ({ ...s, action: 'impersonated', is_session: true }))]
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      .map((item, idx) => (
                        <div key={idx} className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex items-start space-x-3">
                          <div className={`p-2 rounded-lg ${item.is_session ? 'bg-amber-100' : 'bg-blue-100'}`}>
                            {item.is_session ? <LogIn className="w-4 h-4 text-amber-600" /> : <Clock className="w-4 h-4 text-blue-600" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between">
                              <span className="text-sm font-bold text-gray-900">
                                {item.action?.replace(/_/g, ' ') || 'Action'}
                              </span>
                              <span className="text-xs text-gray-400">
                                {new Date(item.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {item.admin_email || item.details?.admin_email || 'Admin'}
                            </div>
                            {item.details && Object.keys(item.details).length > 0 && (
                              <div className="mt-2 p-2 bg-gray-50 rounded text-[10px] text-gray-400 font-mono break-all">
                                {JSON.stringify(item.details)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
};

const ImpersonateButton = ({ advisor, onImpersonate }) => {
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClick = async (e) => {
    e.stopPropagation();
    if (!confirming) {
      setConfirming(true);
      setTimeout(() => setConfirming(false), 3000);
      return;
    }

    setLoading(true);
    try {
      await onImpersonate(advisor.user_id || advisor.id, `${advisor.first_name} ${advisor.last_name}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${
        confirming 
          ? 'bg-amber-100 border-amber-400 text-amber-700' 
          : 'border-primary text-primary hover:bg-primary hover:text-white'
      }`}
    >
      {loading ? 'Signing in...' : confirming ? 'Are you sure?' : 'Login as →'}
    </button>
  );
};

const AdminAdvisors = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [counts, setCounts] = useState({ all: 0, active: 0, incomplete: 0, suspended: 0 });
  const [showHideModal, setShowHideModal] = useState(false);
  const [hidingAdvisor, setHidingAdvisor] = useState(null);
  const [hideReason, setHideReason] = useState('');
  const [showCompModal, setShowCompModal] = useState(false);
  const [compAdvisor, setCompAdvisor] = useState(null);
  const [compMonths, setCompMonths] = useState('3');
  const [compCustomDate, setCompCustomDate] = useState('');
  const [compNote, setCompNote] = useState('');
  const [applyingComp, setApplyingComp] = useState(false);
  const [removingComp, setRemovingComp] = useState(null);
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg, type = 'success') => setToast({ message: msg, type }), []);

  const statusFilter = searchParams.get('status') || 'all';

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchAdvisors();
    fetchCategories();
  }, [user, statusFilter, categoryFilter, subscriptionFilter]);

  const [selectedAdvisorDetail, setSelectedAdvisorDetail] = useState(null);

  const fetchAdvisors = async () => {
    try {
      let url = `/api/admin/advisors?status=${statusFilter}`;
      if (categoryFilter !== 'all') url += `&category=${categoryFilter}`;
      if (subscriptionFilter !== 'all') url += `&subscription=${subscriptionFilter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAdvisors(data.advisors || data);
        setCounts(data.counts || counts);
      }
    } catch (error) {
      console.error('Error fetching advisors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/reference/categories', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSuspend = async (advisorId, currentStatus) => {
    const action = currentStatus === 'suspended' ? 'unsuspend' : 'suspend';
    const reason = currentStatus !== 'suspended' ? prompt('Reason for suspension:') : null;
    
    if (currentStatus !== 'suspended' && !reason) return;

    try {
      const response = await fetch(`/api/admin/advisors/${advisorId}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      
      if (response.ok) {
        alert(`Advisor ${action}ed successfully`);
        fetchAdvisors();
      }
    } catch (error) {
      console.error(`Error ${action}ing advisor:`, error);
    }
    setOpenActionMenu(null);
  };

  const handleDelete = async (advisorId, name) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${name}? This cannot be undone.`)) return;

    try {
      const response = await fetch(`/api/admin/advisors/${advisorId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        alert('Advisor deleted');
        fetchAdvisors();
      }
    } catch (error) {
      console.error('Error deleting advisor:', error);
    }
    setOpenActionMenu(null);
  };

  const handleImpersonate = async (advisorId, name) => {
    if (!window.confirm(`Sign in as ${name}? You'll be logged out and logged in as this advisor.`)) return;

    try {
      const response = await fetch(`/api/admin/impersonate/${advisorId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        alert(`Now signed in as ${name}`);
        window.location.href = '/advisor-portal';
      }
    } catch (error) {
      console.error('Error impersonating:', error);
    }
  };

  const handleChangeSubscription = async (advisorId, newStatus) => {
    const reason = prompt('Reason for change (required):');
    if (!reason) return;

    try {
      const response = await fetch(`/api/admin/advisors/${advisorId}/subscription`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, reason })
      });
      
      if (response.ok) {
        alert('Subscription updated');
        fetchAdvisors();
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
    }
    setOpenActionMenu(null);
  };

  const handleApplyComp = async () => {
    if (!compAdvisor) return;
    setApplyingComp(true);
    try {
      const body = compMonths === 'custom'
        ? { until: compCustomDate, note: compNote }
        : { months: parseInt(compMonths, 10), note: compNote };
      const res = await fetch(`/api/admin/advisors/${compAdvisor.id}/comp`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        setShowCompModal(false);
        setCompAdvisor(null);
        setCompMonths('3');
        setCompCustomDate('');
        setCompNote('');
        fetchAdvisors();
        showToast(`Comp applied to ${compAdvisor.first_name}`);
      } else {
        showToast(data.error || 'Failed to apply comp', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    } finally {
      setApplyingComp(false);
    }
  };

  const handleRemoveComp = async (advisor) => {
    setRemovingComp(advisor.id);
    try {
      const res = await fetch(`/api/admin/advisors/${advisor.id}/remove-comp`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok) {
        fetchAdvisors();
        showToast(`Comp removed for ${advisor.first_name}`);
      } else {
        showToast(data.error || 'Failed to remove comp', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    } finally {
      setRemovingComp(null);
      setOpenActionMenu(null);
    }
  };

  const handleVisibilityToggle = async (advisor) => {
    const currentlyVisible = advisor.visible !== false;
    if (currentlyVisible) {
      setHidingAdvisor(advisor);
      setHideReason('');
      setShowHideModal(true);
    } else {
      try {
        const res = await fetch(`/api/admin/advisors/${advisor.id}/visibility`, {
          method: 'PATCH', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ visible: true })
        });
        if (res.ok) fetchAdvisors();
      } catch (e) { console.error('Visibility toggle error:', e); }
    }
    setOpenActionMenu(null);
  };

  const confirmHideAdvisor = async () => {
    if (!hidingAdvisor) return;
    try {
      const res = await fetch(`/api/admin/advisors/${hidingAdvisor.id}/visibility`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visible: false, reason: hideReason })
      });
      if (res.ok) {
        setShowHideModal(false);
        setHidingAdvisor(null);
        setHideReason('');
        fetchAdvisors();
      }
    } catch (e) { console.error('Hide advisor error:', e); }
  };

  const getStatusIndicator = (advisor) => {
    if (advisor.suspended_at) {
      return <span className="flex items-center text-red-600"><span className="w-2 h-2 bg-red-600 rounded-full mr-1"></span>Suspended</span>;
    }
    if (advisor.is_visible === false) {
      return <span className="flex items-center text-gray-400"><span className="w-2 h-2 bg-gray-400 rounded-full mr-1"></span>Hidden</span>;
    }
    if (advisor.status === 'approved' || advisor.is_visible) {
      return <span className="flex items-center text-green-600"><span className="w-2 h-2 bg-green-600 rounded-full mr-1"></span>Active</span>;
    }
    return <span className="flex items-center text-yellow-600"><span className="w-2 h-2 bg-yellow-600 rounded-full mr-1"></span>Incomplete</span>;
  };

  const getSubscriptionBadge = (advisor) => {
    const planType = advisor.plan_type;
    const subStatus = advisor.subscription_status;
    if (planType === 'comped' || subStatus === 'comped') {
      if (advisor.comped_until) {
        const expiry = new Date(advisor.comped_until);
        if (expiry > new Date()) {
          const label = expiry.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          return <span className="flex items-center text-sm text-yellow-700"><Star className="w-4 h-4 text-yellow-500 mr-1" />Comped until {label}</span>;
        }
        return <span className="flex items-center text-sm text-red-600"><AlertTriangle className="w-4 h-4 mr-1" />Comp Expired</span>;
      }
      return <span className="flex items-center text-sm"><Star className="w-4 h-4 text-yellow-500 mr-1" />Comped</span>;
    }
    switch (planType || subStatus || 'beta') {
      case 'beta':
        return <span className="flex items-center text-sm"><Gift className="w-4 h-4 text-green-500 mr-1" />Beta</span>;
      case 'active':
      case 'paid':
      case 'monthly':
        return <span className="flex items-center text-sm"><CreditCard className="w-4 h-4 text-blue-500 mr-1" />Paid</span>;
      default:
        return <span className="flex items-center text-sm"><Gift className="w-4 h-4 text-gray-400 mr-1" />{planType || subStatus || 'beta'}</span>;
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase() || '??';
  };

  const filteredAdvisors = advisors.filter(advisor => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (advisor.first_name?.toLowerCase() || '').includes(search) ||
      (advisor.last_name?.toLowerCase() || '').includes(search) ||
      (advisor.email?.toLowerCase() || '').includes(search)
    );
  });

  return (
    <AdminLayout activeTab="advisors">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Advisors</h1>
        </div>

        <div className="flex items-center space-x-2">
          {[
            { key: 'all', label: `All (${counts.all || advisors.length})` },
            { key: 'active', label: `Active (${counts.active || 0})` },
            { key: 'incomplete', label: `Incomplete (${counts.incomplete || 0})` },
            { key: 'suspended', label: `Suspended (${counts.suspended || 0})` }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setSearchParams({ status: tab.key })}
              className={`px-4 py-2 text-sm font-medium rounded-2xl transition-colors ${
                statusFilter === tab.key
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center space-x-4">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border border-gray-300 rounded-2xl px-3 py-2 text-sm"
          >
            <option value="all">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id || cat.slug} value={cat.slug || cat.id}>{cat.display_name || cat.name}</option>
            ))}
          </select>

          <select
            value={subscriptionFilter}
            onChange={(e) => setSubscriptionFilter(e.target.value)}
            className="border border-gray-300 rounded-2xl px-3 py-2 text-sm"
          >
            <option value="all">All Subscriptions</option>
            <option value="beta">Beta</option>
            <option value="comped">Comped</option>
            <option value="paid">Paid</option>
          </select>

          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search advisors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-2xl text-sm"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredAdvisors.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
            <p className="text-gray-500">No advisors found</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-transparent">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Advisor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profile</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAdvisors.map((advisor) => (
                  <tr key={advisor.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedAdvisorDetail(advisor)}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold overflow-hidden flex-shrink-0" style={{ background: '#004D35', color: '#fff' }}>
                          {advisor.profile_photo_url ? (
                            <img src={advisor.profile_photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            getInitials(advisor.first_name, advisor.last_name)
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {advisor.first_name} {advisor.last_name}
                          </div>
                          <div className="text-sm text-gray-500">{advisor.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {advisor.title || advisor.specialty || 'Not set'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusIndicator(advisor)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        (advisor.profile_completion_pct || 0) >= 80 ? 'bg-green-100 text-green-800' :
                        (advisor.profile_completion_pct || 0) >= 50 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {advisor.profile_completion_pct || 0}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getSubscriptionBadge(advisor)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end space-x-2">
                        <ImpersonateButton advisor={advisor} onImpersonate={handleImpersonate} />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (openActionMenu === advisor.id) {
                              setOpenActionMenu(null);
                              return;
                            }
                            const rect = e.currentTarget.getBoundingClientRect();
                            const dropdownHeight = 340;
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const top = spaceBelow < dropdownHeight
                              ? rect.top - dropdownHeight + window.scrollY
                              : rect.bottom + window.scrollY + 4;
                            setMenuPosition({ top, right: window.innerWidth - rect.right });
                            setOpenActionMenu(advisor.id);
                          }}
                          className="p-2 hover:bg-gray-100 rounded-2xl"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openActionMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpenActionMenu(null)} />
          {(() => {
            const advisor = filteredAdvisors.find(a => a.id === openActionMenu);
            if (!advisor) return null;
            return (
              <div
                style={{ position: 'fixed', top: menuPosition.top, right: menuPosition.right, zIndex: 50, width: 224 }}
                className="bg-white rounded-2xl shadow-xl border border-gray-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="py-1">
                  <a
                    href={`/advisor/${advisor.profile_slug || advisor.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View Public Profile
                  </a>
                  <button
                    onClick={() => { setSelectedAdvisor(advisor); setOpenActionMenu(null); }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Edit Profile
                  </button>
                  <hr className="my-1" />
                  <button
                    onClick={() => handleImpersonate(advisor.user_id || advisor.id, `${advisor.first_name} ${advisor.last_name}`)}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign In As Advisor
                  </button>
                  <hr className="my-1" />
                  <button
                    onClick={() => { handleChangeSubscription(advisor.id, 'beta'); setOpenActionMenu(null); }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Gift className="w-4 h-4 mr-2" />
                    Change to Beta
                  </button>
                  <button
                    onClick={() => { setCompAdvisor(advisor); setCompMonths('3'); setCompCustomDate(''); setCompNote(''); setShowCompModal(true); setOpenActionMenu(null); }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Change to Comped
                  </button>
                  {advisor.plan_type === 'comped' && (
                    <button
                      onClick={() => handleRemoveComp(advisor)}
                      disabled={removingComp === advisor.id}
                      className="flex items-center w-full px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 disabled:opacity-50"
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      {removingComp === advisor.id ? 'Removing…' : 'Remove Comp'}
                    </button>
                  )}
                  <hr className="my-1" />
                  <button
                    onClick={() => { handleVisibilityToggle(advisor); }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    {advisor.is_visible === false ? (
                      <><Eye className="w-4 h-4 mr-2" />Make Visible</>
                    ) : (
                      <><EyeOff className="w-4 h-4 mr-2" />Hide from Platform</>
                    )}
                  </button>
                  <hr className="my-1" />
                  <button
                    onClick={() => handleSuspend(advisor.id, advisor.suspended_at ? 'suspended' : 'active')}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    {advisor.suspended_at ? (
                      <><Eye className="w-4 h-4 mr-2" />Unsuspend Advisor</>
                    ) : (
                      <><EyeOff className="w-4 h-4 mr-2" />Suspend Advisor</>
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(advisor.id, `${advisor.first_name} ${advisor.last_name}`)}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Advisor
                  </button>
                </div>
              </div>
            );
          })()}
        </>
      )}

      {selectedAdvisorDetail && (
        <AdvisorDetailPanel 
          advisor={selectedAdvisorDetail} 
          onClose={() => setSelectedAdvisorDetail(null)} 
          onRefresh={fetchAdvisors}
        />
      )}

      {/* Comp Modal */}
      {showCompModal && compAdvisor && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCompModal(false)} />
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-7 relative">
            <h2 className="text-xl font-serif text-gray-900 mb-1">Apply comp period</h2>
            <p className="text-sm text-gray-500 mb-5">
              Comp <span className="font-semibold">{compAdvisor.first_name} {compAdvisor.last_name}</span> for free.
              {compAdvisor.stripe_subscription_id && ' A 100% Stripe coupon will be applied to their subscription.'}
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-2">How many months?</label>
                <select
                  value={compMonths}
                  onChange={(e) => setCompMonths(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-3.5 py-3 text-sm focus:ring-2 focus:ring-[#004D35] focus:border-transparent outline-none bg-white"
                >
                  <option value="1">1 month</option>
                  <option value="2">2 months</option>
                  <option value="3">3 months</option>
                  <option value="6">6 months</option>
                  <option value="12">12 months</option>
                  <option value="custom">Custom date…</option>
                </select>
              </div>
              {compMonths === 'custom' && (
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-2">Comp ends on</label>
                  <input
                    type="date"
                    value={compCustomDate}
                    onChange={(e) => setCompCustomDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full border border-gray-300 rounded-xl px-3.5 py-3 text-sm focus:ring-2 focus:ring-[#004D35] focus:border-transparent outline-none"
                  />
                </div>
              )}
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-2">Internal note (optional)</label>
                <input
                  type="text"
                  value={compNote}
                  onChange={(e) => setCompNote(e.target.value)}
                  placeholder="e.g. Early beta incentive, partnership deal…"
                  className="w-full border border-gray-300 rounded-xl px-3.5 py-3 text-sm focus:ring-2 focus:ring-[#004D35] focus:border-transparent outline-none"
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setShowCompModal(false)}
                  className="flex-1 px-5 py-3 border border-gray-300 text-gray-600 font-semibold rounded-full hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyComp}
                  disabled={applyingComp || (compMonths === 'custom' && !compCustomDate)}
                  className="flex-1 px-5 py-3 bg-[#004D35] text-white font-semibold rounded-full hover:bg-[#003d2a] disabled:opacity-50 transition-colors"
                >
                  {applyingComp ? 'Applying…' : 'Apply Comp'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hide from Platform Modal */}
      {showHideModal && hidingAdvisor && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowHideModal(false)} />
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-7 relative">
            <h2 className="text-xl font-serif text-gray-900 mb-1">Hide from platform</h2>
            <p className="text-sm text-gray-500 mb-5">
              <span className="font-semibold">{hidingAdvisor.first_name} {hidingAdvisor.last_name}</span> will no longer appear publicly on Viysr. You can restore them at any time.
            </p>
            <div className="space-y-5">
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-2">
                  Reason (optional — internal only)
                </label>
                <input
                  type="text"
                  value={hideReason}
                  onChange={(e) => setHideReason(e.target.value)}
                  placeholder="e.g. Inactive profile, license issue..."
                  className="w-full border border-gray-300 rounded-xl px-3.5 py-3 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowHideModal(false)}
                  className="flex-1 px-5 py-3 border border-gray-300 text-gray-600 font-semibold rounded-full hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmHideAdvisor}
                  className="flex-1 px-5 py-3 bg-gray-900 text-white font-semibold rounded-full hover:bg-gray-700 transition-colors"
                >
                  Hide advisor
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </AdminLayout>
  );
};

export default AdminAdvisors;
